import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut,
  Phone,
  Mail,
  Building2,
  RefreshCw,
  Trash2,
  Loader2,
  Download,
} from "lucide-react";
import { toast } from "sonner";
import { api, tokenStore, formatApiError, API } from "@/lib/api";

const STATUSES = [
  { v: "new", l: "Nueva" },
  { v: "contacted", l: "Contactada" },
  { v: "closed", l: "Cerrada" },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter !== "all" ? { status_filter: filter } : {};
      const [qRes, sRes] = await Promise.all([
        api.get("/admin/quotes", { params }),
        api.get("/admin/stats"),
      ]);
      setQuotes(qRes.data);
      setStats(sRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        tokenStore.clear();
        navigate("/admin");
        return;
      }
      toast.error("Error al cargar", {
        description: formatApiError(err.response?.data?.detail) || err.message,
      });
    } finally {
      setLoading(false);
    }
  }, [filter, navigate]);

  useEffect(() => {
    if (!tokenStore.get()) {
      navigate("/admin");
      return;
    }
    load();
  }, [load, navigate]);

  const logout = () => {
    tokenStore.clear();
    navigate("/admin");
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/quotes/${id}`, { status });
      toast.success("Estado actualizado");
      load();
    } catch (err) {
      toast.error("No se pudo actualizar", {
        description: formatApiError(err.response?.data?.detail) || err.message,
      });
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta cotización? No se puede deshacer.")) return;
    try {
      await api.delete(`/admin/quotes/${id}`);
      toast.success("Cotización eliminada");
      load();
    } catch (err) {
      toast.error("No se pudo eliminar", {
        description: formatApiError(err.response?.data?.detail) || err.message,
      });
    }
  };

  const exportCSV = async () => {
    try {
      const params = filter !== "all" ? { status_filter: filter } : {};
      const res = await api.get("/admin/quotes/export", {
        params,
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ts = new Date().toISOString().slice(0, 16).replace(/[:T-]/g, "");
      a.href = url;
      a.download = `vigopal-cotizaciones-${ts}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV descargado");
    } catch (err) {
      toast.error("No se pudo exportar", {
        description: formatApiError(err.response?.data?.detail) || err.message,
      });
    }
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-vp-navy text-white">
      <header className="bg-vp-navy-deep border-b-2 border-vp-yellow sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/logo-vigopal.png"
              alt="Vigopal"
              className="w-9 h-9 object-contain border-2 border-vp-yellow"
            />
            <div>
              <div className="heading-industrial text-lg leading-none text-white">VIGOPAL</div>
              <div className="label-industrial text-[10px]">Panel interno</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={exportCSV}
              data-testid="dashboard-export-csv"
              className="inline-flex items-center gap-2 px-3 py-2 border-2 border-vp-yellow text-white hover:bg-vp-yellow hover:text-[#001E62] font-bold uppercase tracking-widest text-xs transition-colors"
            >
              <Download size={14} />
              <span className="hidden sm:inline">CSV</span>
            </button>
            <button
              onClick={load}
              data-testid="dashboard-refresh"
              className="p-2 border-2 border-vp-yellow text-white hover:bg-vp-yellow hover:text-[#001E62] transition-colors"
              aria-label="Recargar"
            >
              <RefreshCw size={16} />
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 bg-vp-red hover:bg-vp-yellow hover:text-[#001E62] text-white font-bold uppercase tracking-widest text-xs px-4 py-2 transition-colors"
            >
              <LogOut size={14} />
              Salir
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8 lg:py-12">
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-6 border-2 border-vp-yellow bg-vp-navy-deep mb-8">
            {[
              { l: "Total", v: stats.total, c: "text-white" },
              { l: "Nuevas", v: stats.new, c: "text-vp-yellow" },
              { l: "Contactadas", v: stats.contacted, c: "text-amber-400" },
              { l: "Cerradas", v: stats.closed, c: "text-emerald-400" },
              { l: "Compras", v: stats.comprar, c: "text-white" },
              { l: "Ventas", v: stats.vender, c: "text-white" },
            ].map((s, i) => (
              <div
                key={s.l}
                className={`px-5 py-6 ${i < 5 ? "lg:border-r-2 border-white/15" : ""} border-b-2 lg:border-b-0 border-white/15`}
              >
                <div className="label-industrial mb-2">{s.l}</div>
                <div className={`heading-industrial text-3xl ${s.c}`}>{s.v}</div>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-0 border-2 border-vp-yellow w-fit mb-6 bg-vp-navy-deep">
          {[{ v: "all", l: "Todas" }, ...STATUSES].map((f, i) => (
            <button
              key={f.v}
              onClick={() => setFilter(f.v)}
              data-testid={`filter-${f.v}`}
              className={`px-5 py-3 font-bold uppercase tracking-widest text-xs transition-colors ${i > 0 ? "border-l-2 border-white/15" : ""} ${filter === f.v ? "bg-vp-yellow text-[#001E62]" : "text-white hover:bg-vp-navy-soft"}`}
            >
              {f.l}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="bg-vp-navy-deep border-2 border-vp-yellow p-12 flex items-center justify-center text-vp-yellow">
            <Loader2 className="animate-spin" />
          </div>
        ) : quotes.length === 0 ? (
          <div className="bg-vp-navy-deep border-2 border-vp-yellow p-12 text-center">
            <div className="heading-industrial text-2xl text-white/30 mb-2">SIN COTIZACIONES</div>
            <p className="text-white/60 text-sm">Las solicitudes enviadas desde el sitio aparecerán aquí.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {quotes.map((q) => (
              <article
                key={q.id}
                data-testid={`quote-card-${q.id}`}
                className="bg-vp-navy-deep border-2 border-vp-yellow grid grid-cols-1 lg:grid-cols-12"
              >
                <div className="lg:col-span-8 p-6 lg:border-r-2 border-white/15">
                  <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span
                          className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${q.request_type === "comprar" ? "bg-vp-yellow text-[#001E62]" : "bg-vp-red text-white"}`}
                        >
                          {q.request_type === "comprar" ? "Comprar" : "Vender"}
                        </span>
                        <span className="label-industrial">
                          {new Date(q.created_at).toLocaleString("es-CL")}
                        </span>
                      </div>
                      <h3 className="heading-industrial text-2xl text-white">{q.name}</h3>
                      <div className="text-white/75 mt-1 flex items-center gap-2">
                        <Building2 size={14} className="text-vp-yellow" />
                        {q.company}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="label-industrial">Cantidad</div>
                      <div className="font-bold text-lg text-white">{q.quantity}</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    <a href={`tel:${q.phone}`} className="flex items-center gap-2 text-white/85 hover:text-vp-yellow">
                      <Phone size={14} className="text-vp-yellow" />
                      {q.phone}
                    </a>
                    {q.email && (
                      <a href={`mailto:${q.email}`} className="flex items-center gap-2 text-white/85 hover:text-vp-yellow break-all">
                        <Mail size={14} className="text-vp-yellow" />
                        {q.email}
                      </a>
                    )}
                  </div>

                  {q.message && (
                    <div className="mt-4 p-4 bg-vp-navy border-l-4 border-vp-yellow text-sm text-white/85">
                      {q.message}
                    </div>
                  )}
                </div>

                <div className="lg:col-span-4 p-6 bg-vp-navy flex flex-col justify-between gap-4 border-t-2 lg:border-t-0 border-white/15">
                  <div>
                    <div className="label-industrial mb-3">Estado</div>
                    <div className="flex flex-col gap-2">
                      {STATUSES.map((s) => (
                        <button
                          key={s.v}
                          onClick={() => updateStatus(q.id, s.v)}
                          data-testid={`status-${s.v}-${q.id}`}
                          className={`text-left px-3 py-2 text-xs font-bold uppercase tracking-widest border-2 transition-colors ${q.status === s.v ? "bg-vp-yellow text-[#001E62] border-vp-yellow" : "bg-transparent text-white border-white/30 hover:border-vp-yellow"}`}
                        >
                          {s.l}
                        </button>
                      ))}
                    </div>
                  </div>
                  <button
                    onClick={() => remove(q.id)}
                    data-testid={`delete-quote-${q.id}`}
                    className="inline-flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-widest text-vp-red hover:text-white hover:bg-vp-red border-2 border-vp-red px-3 py-2 transition-colors"
                  >
                    <Trash2 size={14} />
                    Eliminar
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
